import {
  createDungeonCombatPatternSeed,
  selectDungeonIntent,
} from "./dungeon-combat";
import { getDungeonEncounterProfile } from "./dungeon-challenges";
import type {
  DungeonActionKind,
  DungeonIntentKind,
} from "./dungeon-combat";

export * from "./dungeon-combat";
export * from "./dungeon-challenges";
export * from "./dungeon-loot";

export type DungeonDifficulty = "explorer" | "adventurer" | "nightmare";

export type DungeonDirection = "north" | "east" | "south" | "west";

export type DungeonEncounterKind =
  | "entrance"
  | "monster"
  | "trap"
  | "cache"
  | "boss";

export interface DungeonCell {
  id: string;
  x: number;
  y: number;
  exits: DungeonDirection[];
  encounterKind: DungeonEncounterKind;
  danger: 0 | 1 | 2 | 3;
  distanceFromEntrance: number;
}

export interface DungeonMaze {
  version: DungeonMazeVersion;
  seed: number;
  size: number;
  entranceId: string;
  exitId: string;
  cells: DungeonCell[];
}

export const DUNGEON_MAZE_VERSION = 2 as const;

export type DungeonMazeVersion = 1 | typeof DUNGEON_MAZE_VERSION;

export interface DungeonMazeMetrics {
  edgeCount: number;
  deadEndCount: number;
  junctionCount: number;
  cornerCount: number;
  entranceOptions: number;
  exitDistance: number;
}

export interface DungeonDifficultyProfile {
  difficulty: DungeonDifficulty;
  label: string;
  maxHealth: number;
  reactionWindowMs: number;
  mistakeDamage: number;
  timeoutDamage: number;
  scoreMultiplier: number;
}

export interface DungeonEncounterPlan {
  kind: DungeonEncounterKind;
  wordCount: number;
  reactionWindowMs: number | null;
  damageOnMistake: number;
  damageOnTimeout: number;
  reward: number;
}

export interface DungeonActionOffer {
  kind: DungeonActionKind;
  label: string;
  word: string;
  progress: 1 | 3;
  ward: number;
  timeBonusMs: number;
  scoreMultiplier: number;
  description: string;
}

const DUNGEON_ACTION_FALLBACK_WORDS = [
  "ward",
  "brace",
  "guard",
  "focus",
  "steady",
  "strike",
  "channel",
  "advance",
  "momentum",
  "concentration",
] as const;

const DUNGEON_ACTION_LABELS: Record<
  DungeonEncounterKind,
  Record<DungeonActionKind, string>
> = {
  entrance: {
    assault: "Press Forward",
    defense: "Scout Ahead",
    focus: "Read the Passage",
  },
  monster: {
    assault: "Power Strike",
    defense: "Raise Guard",
    focus: "Find an Opening",
  },
  trap: {
    assault: "Sever the Trigger",
    defense: "Bypass the Hazard",
    focus: "Decode the Pattern",
  },
  cache: {
    assault: "Force the Lock",
    defense: "Ward the Seal",
    focus: "Trace the Sigil",
  },
  boss: {
    assault: "Shatter the Ward",
    defense: "Hold the Line",
    focus: "Read the Echo",
  },
};

const DUNGEON_ACTION_PROGRESS_NOUNS: Record<DungeonEncounterKind, string> = {
  entrance: "passage",
  monster: "strike",
  trap: "mechanism",
  cache: "seal",
  boss: "ward",
};

const DIRECTIONS: ReadonlyArray<{
  id: DungeonDirection;
  dx: number;
  dy: number;
  opposite: DungeonDirection;
}> = [
  { id: "north", dx: 0, dy: -1, opposite: "south" },
  { id: "east", dx: 1, dy: 0, opposite: "west" },
  { id: "south", dx: 0, dy: 1, opposite: "north" },
  { id: "west", dx: -1, dy: 0, opposite: "east" },
];

const DIFFICULTY_PROFILES: Record<
  DungeonDifficulty,
  DungeonDifficultyProfile
> = {
  explorer: {
    difficulty: "explorer",
    label: "Explorer",
    maxHealth: 120,
    reactionWindowMs: 12_000,
    mistakeDamage: 2,
    timeoutDamage: 8,
    scoreMultiplier: 0.8,
  },
  adventurer: {
    difficulty: "adventurer",
    label: "Adventurer",
    maxHealth: 100,
    reactionWindowMs: 8_500,
    mistakeDamage: 4,
    timeoutDamage: 12,
    scoreMultiplier: 1,
  },
  nightmare: {
    difficulty: "nightmare",
    label: "Hero",
    maxHealth: 80,
    reactionWindowMs: 6_000,
    mistakeDamage: 7,
    timeoutDamage: 18,
    scoreMultiplier: 1.45,
  },
};

function mulberry32(seed: number): () => number {
  let value = seed >>> 0;
  return () => {
    value += 0x6d2b79f5;
    let result = value;
    result = Math.imul(result ^ (result >>> 15), result | 1);
    result ^= result + Math.imul(result ^ (result >>> 7), result | 61);
    return ((result ^ (result >>> 14)) >>> 0) / 4_294_967_296;
  };
}

function shuffle<T>(items: readonly T[], random: () => number): T[] {
  const result = [...items];
  for (let index = result.length - 1; index > 0; index -= 1) {
    const target = Math.floor(random() * (index + 1));
    const indexedValue = result[index];
    const targetValue = result[target];
    if (indexedValue !== undefined && targetValue !== undefined) {
      result[index] = targetValue;
      result[target] = indexedValue;
    }
  }
  return result;
}

function cellId(x: number, y: number): string {
  return `${x}:${y}`;
}

function compareDungeonActionWords(left: string, right: string): number {
  if (left.length !== right.length) return left.length - right.length;
  return left < right ? -1 : left > right ? 1 : 0;
}

function normalizeDungeonActionWords(candidateWords: readonly string[]): string[] {
  const seen = new Set<string>();
  const normalized: string[] = [];
  for (const candidate of candidateWords) {
    if (typeof candidate !== "string") continue;
    const word = candidate.normalize("NFKC").trim().replace(/\s+/g, " ").toLowerCase();
    if (!word || seen.has(word)) continue;
    seen.add(word);
    normalized.push(word);
  }
  return normalized;
}

function dungeonActionInitial(word: string): string {
  return word.match(/[a-z0-9]/i)?.[0]?.toLowerCase() ?? word.charAt(0);
}

function chooseDungeonActionWords(candidateWords: readonly string[]): {
  assault: string;
  defense: string;
  focus: string;
} {
  const normalized = normalizeDungeonActionWords(candidateWords);
  const pool = [...normalized];
  if (pool.length < 3) {
    const seen = new Set(pool);
    for (const fallback of DUNGEON_ACTION_FALLBACK_WORDS) {
      if (seen.has(fallback)) continue;
      seen.add(fallback);
      pool.push(fallback);
    }
  }
  pool.sort(compareDungeonActionWords);

  const shortest = pool[0] ?? "ward";
  const shortestInitial = dungeonActionInitial(shortest);
  const distinctInitialCount = new Set(pool.map(dungeonActionInitial)).size;
  const longest = distinctInitialCount >= 2
    ? [...pool].reverse().find(
        (word) => word !== shortest && dungeonActionInitial(word) !== shortestInitial,
      ) ?? pool[pool.length - 1] ?? "concentration"
    : pool[pool.length - 1] ?? "concentration";
  const longestInitial = dungeonActionInitial(longest);
  const midpoint = (shortest.length + longest.length) / 2;
  const focusCandidates = pool.filter(
    (word) => word !== shortest && word !== longest,
  );
  focusCandidates.sort((left, right) => {
    const leftDistinct =
      dungeonActionInitial(left) !== shortestInitial &&
      dungeonActionInitial(left) !== longestInitial;
    const rightDistinct =
      dungeonActionInitial(right) !== shortestInitial &&
      dungeonActionInitial(right) !== longestInitial;
    if (distinctInitialCount >= 3 && leftDistinct !== rightDistinct) {
      return leftDistinct ? -1 : 1;
    }
    const distanceDifference =
      Math.abs(left.length - midpoint) - Math.abs(right.length - midpoint);
    return distanceDifference || compareDungeonActionWords(left, right);
  });

  const selected = [
    shortest,
    focusCandidates[0] ?? "focus",
    longest,
  ].sort(compareDungeonActionWords);
  return {
    defense: selected[0] ?? "ward",
    focus: selected[1] ?? "focus",
    assault: selected[2] ?? "concentration",
  };
}

/**
 * Creates three deterministic tactical choices from a word pool. Word length
 * communicates the action's risk: defense is shortest, focus is intermediate,
 * and the higher-value assault is longest. Distinct opening letters are used
 * whenever the supplied vocabulary can provide them.
 */
export function createDungeonActionOffers(
  encounterKind: DungeonEncounterKind,
  danger: number,
  candidateWords: readonly string[],
): DungeonActionOffer[] {
  const safeEncounterKind = DUNGEON_ACTION_LABELS[encounterKind]
    ? encounterKind
    : "monster";
  const safeDanger = Number.isFinite(danger)
    ? Math.min(3, Math.max(0, Math.floor(danger)))
    : 1;
  const labels = DUNGEON_ACTION_LABELS[safeEncounterKind];
  const progressNoun = DUNGEON_ACTION_PROGRESS_NOUNS[safeEncounterKind];
  const words = chooseDungeonActionWords(candidateWords);
  const isTrap = safeEncounterKind === "trap";
  const ward = isTrap ? 0 : 1;
  const timeBonusMs = 2_000 + safeDanger * 500;

  return [
    {
      kind: "assault",
      label: labels.assault,
      word: words.assault,
      progress: isTrap ? 1 : 3,
      ward: 0,
      timeBonusMs: 0,
      scoreMultiplier: 1.5,
      description: isTrap
        ? "Sever the active trigger before the mechanism fires."
        : `Deal 3 ${progressNoun} damage, or 5 while an opening is ready.`,
    },
    {
      kind: "defense",
      label: labels.defense,
      word: words.defense,
      progress: 1,
      ward,
      timeBonusMs: 0,
      scoreMultiplier: 1,
      description: isTrap
        ? "Bypass the active hazard without engaging the mechanism."
        : `Deal 1 ${progressNoun} damage and bank up to 2 wards.`,
    },
    {
      kind: "focus",
      label: labels.focus,
      word: words.focus,
      progress: 1,
      ward: 0,
      timeBonusMs,
      scoreMultiplier: 1,
      description: isTrap
        ? `Decode the active pattern and gain ${(timeBonusMs / 1_000).toFixed(1)} seconds.`
        : `Deal 1 ${progressNoun} damage, expose an opening, and gain ${(timeBonusMs / 1_000).toFixed(1)} seconds.`,
    },
  ];
}

export function getDungeonOpeningIntent(
  cell: DungeonCell,
  seed: number,
): DungeonIntentKind {
  const patternSeed = createDungeonCombatPatternSeed(seed, cell.id);
  return selectDungeonIntent({
    patternSeed,
    patternCursor: 0,
    encounterKind: cell.encounterKind,
    previousIntent: null,
    lastPlayerAction: null,
  });
}

function encounterForDistance(
  distance: number,
  randomValue: number,
): Pick<DungeonCell, "encounterKind" | "danger"> {
  const danger = Math.min(3, Math.max(1, Math.ceil(distance / 4))) as 1 | 2 | 3;
  if (randomValue < 0.55) {
    return { encounterKind: "monster", danger };
  }
  if (randomValue < 0.82) {
    return { encounterKind: "trap", danger };
  }
  return { encounterKind: "cache", danger: Math.max(1, danger - 1) as 1 | 2 };
}

function carvePerfectDungeonMaze(
  normalizedSize: number,
  random: () => number,
): Map<string, Set<DungeonDirection>> {
  const entrance = { x: 0, y: normalizedSize - 1 };
  const carved = new Map<string, Set<DungeonDirection>>();
  const visited = new Set<string>();
  const stack = [entrance];
  visited.add(cellId(entrance.x, entrance.y));

  while (stack.length > 0) {
    const current = stack[stack.length - 1];
    if (!current) break;
    const available = shuffle(DIRECTIONS, random).filter((direction) => {
      const nextX = current.x + direction.dx;
      const nextY = current.y + direction.dy;
      return (
        nextX >= 0 &&
        nextY >= 0 &&
        nextX < normalizedSize &&
        nextY < normalizedSize &&
        !visited.has(cellId(nextX, nextY))
      );
    });
    const direction = available[0];
    if (!direction) {
      stack.pop();
      continue;
    }

    const next = {
      x: current.x + direction.dx,
      y: current.y + direction.dy,
    };
    const currentId = cellId(current.x, current.y);
    const nextId = cellId(next.x, next.y);
    if (!carved.has(currentId)) carved.set(currentId, new Set());
    if (!carved.has(nextId)) carved.set(nextId, new Set());
    carved.get(currentId)?.add(direction.id);
    carved.get(nextId)?.add(direction.opposite);
    visited.add(nextId);
    stack.push(next);
  }

  return carved;
}

function carveBranchingDungeonMaze(
  normalizedSize: number,
  random: () => number,
): Map<string, Set<DungeonDirection>> {
  const carved = new Map<string, Set<DungeonDirection>>();
  const parents = new Map<string, string>();
  const edges: Array<{
    from: string;
    to: string;
    direction: DungeonDirection;
    opposite: DungeonDirection;
  }> = [];
  for (let y = 0; y < normalizedSize; y += 1) {
    for (let x = 0; x < normalizedSize; x += 1) {
      const id = cellId(x, y);
      carved.set(id, new Set());
      parents.set(id, id);
      if (x + 1 < normalizedSize) {
        edges.push({
          from: id,
          to: cellId(x + 1, y),
          direction: "east",
          opposite: "west",
        });
      }
      if (y + 1 < normalizedSize) {
        edges.push({
          from: id,
          to: cellId(x, y + 1),
          direction: "south",
          opposite: "north",
        });
      }
    }
  }

  const findRoot = (id: string): string => {
    const parent = parents.get(id) ?? id;
    if (parent === id) return id;
    const root = findRoot(parent);
    parents.set(id, root);
    return root;
  };
  let connectedEdges = 0;
  for (const edge of shuffle(edges, random)) {
    const fromRoot = findRoot(edge.from);
    const toRoot = findRoot(edge.to);
    if (fromRoot === toRoot) continue;
    parents.set(toRoot, fromRoot);
    carved.get(edge.from)?.add(edge.direction);
    carved.get(edge.to)?.add(edge.opposite);
    connectedEdges += 1;
    if (connectedEdges === normalizedSize * normalizedSize - 1) break;
  }
  return carved;
}

function getDungeonDistances(
  carved: ReadonlyMap<string, ReadonlySet<DungeonDirection>>,
  entranceId: string,
): Map<string, number> {
  const distances = new Map<string, number>([[entranceId, 0]]);
  const queue = [entranceId];
  while (queue.length > 0) {
    const id = queue.shift();
    if (!id) continue;
    const coordinates = id.split(":").map(Number);
    const x = coordinates[0];
    const y = coordinates[1];
    if (x === undefined || y === undefined) continue;
    const distance = distances.get(id) ?? 0;
    for (const directionId of carved.get(id) ?? []) {
      const direction = DIRECTIONS.find((item) => item.id === directionId);
      if (!direction) continue;
      const nextId = cellId(x + direction.dx, y + direction.dy);
      if (distances.has(nextId)) continue;
      distances.set(nextId, distance + 1);
      queue.push(nextId);
    }
  }
  return distances;
}

function isCorner(exits: ReadonlySet<DungeonDirection>): boolean {
  if (exits.size !== 2) return false;
  return !(
    (exits.has("north") && exits.has("south")) ||
    (exits.has("east") && exits.has("west"))
  );
}

function getCarvedMazeMetrics(
  carved: ReadonlyMap<string, ReadonlySet<DungeonDirection>>,
  entranceId: string,
): DungeonMazeMetrics {
  const distances = getDungeonDistances(carved, entranceId);
  const exits = [...carved.values()];
  return {
    edgeCount: exits.reduce((total, roomExits) => total + roomExits.size, 0) / 2,
    deadEndCount: exits.filter((roomExits) => roomExits.size === 1).length,
    junctionCount: exits.filter((roomExits) => roomExits.size >= 3).length,
    cornerCount: exits.filter(isCorner).length,
    entranceOptions: carved.get(entranceId)?.size ?? 0,
    exitDistance: Math.max(...distances.values()),
  };
}

function scoreCarvedMaze(
  metrics: DungeonMazeMetrics,
  roomCount: number,
): number {
  const desiredDistance = Math.max(5, Math.floor(roomCount * 0.55));
  return (
    (metrics.entranceOptions >= 2 ? 100_000 : 0) +
    (metrics.junctionCount >= 2 ? 50_000 : 0) +
    (metrics.deadEndCount >= 4 ? 25_000 : 0) +
    (metrics.cornerCount >= 4 ? 10_000 : 0) +
    (metrics.exitDistance >= desiredDistance ? 5_000 : 0) +
    metrics.entranceOptions * 180 +
    Math.min(metrics.junctionCount, 4) * 110 +
    Math.min(metrics.deadEndCount, 6) * 62 +
    Math.min(metrics.cornerCount, 8) * 18 +
    metrics.exitDistance * 24 -
    Math.max(0, 2 - metrics.junctionCount) * 260 -
    Math.max(0, 4 - metrics.deadEndCount) * 120 -
    Math.max(0, desiredDistance - metrics.exitDistance) * 90
  );
}

function selectCarvedDungeonMaze(
  normalizedSeed: number,
  normalizedSize: number,
  version: DungeonMazeVersion,
): {
  carved: Map<string, Set<DungeonDirection>>;
  contentRandom: () => number;
} {
  if (version === 1) {
    // Version one intentionally shares its random stream between carving and
    // encounters. Keeping that exact sequence preserves saved legacy runs.
    const legacyRandom = mulberry32(normalizedSeed);
    return {
      carved: carvePerfectDungeonMaze(normalizedSize, legacyRandom),
      contentRandom: legacyRandom,
    };
  }

  const entranceId = cellId(0, normalizedSize - 1);
  const roomCount = normalizedSize * normalizedSize;
  let bestCarved: Map<string, Set<DungeonDirection>> | null = null;
  let bestScore = Number.NEGATIVE_INFINITY;
  const candidateCount = Math.max(48, roomCount * 4);
  for (let attempt = 0; attempt < candidateCount; attempt += 1) {
    const candidateSeed =
      (normalizedSeed ^ Math.imul(attempt + 1, 0x9e3779b1)) >>> 0;
    const candidate = carveBranchingDungeonMaze(
      normalizedSize,
      mulberry32(candidateSeed),
    );
    const metrics = getCarvedMazeMetrics(candidate, entranceId);
    const score = scoreCarvedMaze(metrics, roomCount);
    if (score > bestScore) {
      bestScore = score;
      bestCarved = candidate;
    }
  }

  return {
    carved:
      bestCarved ??
      carveBranchingDungeonMaze(normalizedSize, mulberry32(normalizedSeed)),
    // Content rolls do not shift if maze candidate tuning changes later.
    contentRandom: mulberry32((normalizedSeed ^ 0x51f15e5d) >>> 0),
  };
}

export function analyzeDungeonMaze(maze: DungeonMaze): DungeonMazeMetrics {
  const carved = new Map<string, Set<DungeonDirection>>(
    maze.cells.map((cell) => [cell.id, new Set(cell.exits)]),
  );
  return getCarvedMazeMetrics(carved, maze.entranceId);
}

/**
 * Builds a deterministic perfect maze. Version two evaluates several seeded
 * carvings and selects one with meaningful junctions, false paths, turns, and
 * a long entrance-to-guardian route. Version one remains available solely so
 * checkpoints created by the original generator restore without topology
 * drift.
 */
export function createDungeonMaze(
  seed: number,
  size = 4,
  requestedVersion: number = DUNGEON_MAZE_VERSION,
): DungeonMaze {
  const normalizedSize = Math.min(6, Math.max(3, Math.floor(size)));
  const normalizedSeed = Number.isFinite(seed) ? Math.floor(seed) : 1;
  const version: DungeonMazeVersion = requestedVersion === 1 ? 1 : 2;
  const entrance = { x: 0, y: normalizedSize - 1 };
  const entranceId = cellId(entrance.x, entrance.y);
  const { carved, contentRandom } = selectCarvedDungeonMaze(
    normalizedSeed,
    normalizedSize,
    version,
  );

  const distances = getDungeonDistances(carved, entranceId);

  const exitId = [...distances.entries()].sort(
    ([leftId, leftDistance], [rightId, rightDistance]) =>
      rightDistance - leftDistance || leftId.localeCompare(rightId),
  )[0]?.[0] ?? entranceId;

  const cells: DungeonCell[] = [];
  for (let y = 0; y < normalizedSize; y += 1) {
    for (let x = 0; x < normalizedSize; x += 1) {
      const id = cellId(x, y);
      const distanceFromEntrance = distances.get(id) ?? 0;
      const encounter = encounterForDistance(
        distanceFromEntrance,
        contentRandom(),
      );
      cells.push({
        id,
        x,
        y,
        exits: [...(carved.get(id) ?? [])].sort(
          (left, right) =>
            DIRECTIONS.findIndex((item) => item.id === left) -
            DIRECTIONS.findIndex((item) => item.id === right),
        ),
        encounterKind:
          id === entranceId
            ? "entrance"
            : id === exitId
              ? "boss"
              : encounter.encounterKind,
        danger:
          id === entranceId ? 0 : id === exitId ? 3 : encounter.danger,
        distanceFromEntrance,
      });
    }
  }

  return {
    version,
    seed: normalizedSeed,
    size: normalizedSize,
    entranceId,
    exitId,
    cells,
  };
}

export function getDungeonDifficultyProfile(
  difficulty: DungeonDifficulty,
): DungeonDifficultyProfile {
  return DIFFICULTY_PROFILES[difficulty] ?? DIFFICULTY_PROFILES.adventurer;
}

export function createDungeonEncounterPlan(
  cell: DungeonCell,
  difficulty: DungeonDifficulty,
  runSeed?: number,
): DungeonEncounterPlan {
  const profile = getDungeonDifficultyProfile(difficulty);
  const dangerBonus = Math.max(0, cell.danger - 1);
  const kindWordCounts: Record<DungeonEncounterKind, number> = {
    entrance: 0,
    monster: 10,
    // Traps are short, high-pressure set pieces rather than creatures with a
    // health bar. Danger one exposes one timed mechanism; higher-danger rooms
    // chain two distinct mechanisms before the route is safe.
    trap: Math.min(2, Math.max(1, cell.danger)),
    cache: 9,
    boss: 24,
  };
  const timed = cell.encounterKind !== "entrance" && cell.encounterKind !== "cache";
  const archetype = runSeed === undefined ? null : getDungeonEncounterProfile(runSeed, cell.id, cell.encounterKind);
  if (archetype) {
    const creature = archetype.kind === "monster" || archetype.kind === "boss";
    const puzzleLength = archetype.id === "iron-coffer" || cell.danger >= 3 ? 3 : 2;
    return {
      kind: cell.encounterKind,
      wordCount: creature ? archetype.vitality
        : puzzleLength + (archetype.kind === "trap" ? 3 : 4),
      reactionWindowMs: creature ? Math.max(3_500, profile.reactionWindowMs + archetype.tempo - dangerBonus * 400) : null,
      damageOnMistake: profile.mistakeDamage + dangerBonus,
      damageOnTimeout: profile.timeoutDamage + dangerBonus * 2,
      reward: Math.round((65 + cell.danger * 20 + (creature ? archetype.vitality * 6 : 60)) * profile.scoreMultiplier),
    };
  }
  return {
    kind: cell.encounterKind,
    wordCount: cell.encounterKind === "trap"
      ? kindWordCounts.trap
      : kindWordCounts[cell.encounterKind] +
        (cell.encounterKind === "boss" ? 0 : dangerBonus),
    reactionWindowMs: timed
      ? Math.max(3_500, profile.reactionWindowMs - dangerBonus * 650)
      : null,
    damageOnMistake: profile.mistakeDamage + dangerBonus,
    damageOnTimeout: profile.timeoutDamage + dangerBonus * 2,
    reward: Math.round(
      (35 + cell.danger * 20 + kindWordCounts[cell.encounterKind] * 10) *
        profile.scoreMultiplier,
    ),
  };
}

export function dungeonDestination(
  cell: DungeonCell,
  direction: DungeonDirection,
): { x: number; y: number } | null {
  if (!cell.exits.includes(direction)) return null;
  const contract = DIRECTIONS.find((item) => item.id === direction);
  return contract
    ? { x: cell.x + contract.dx, y: cell.y + contract.dy }
    : null;
}

export function scoreDungeonWord(input: {
  wordLength: number;
  danger: number;
  combo: number;
  firstAttemptCorrect: boolean;
  difficulty: DungeonDifficulty;
}): number {
  const profile = getDungeonDifficultyProfile(input.difficulty);
  const base = Math.max(1, input.wordLength) * 10 + Math.max(0, input.danger) * 15;
  const cleanMultiplier = input.firstAttemptCorrect ? 1.25 : 0.8;
  const comboMultiplier = 1 + Math.min(20, Math.max(0, input.combo)) * 0.04;
  return Math.round(
    base * cleanMultiplier * comboMultiplier * profile.scoreMultiplier,
  );
}
